import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import type { Market, ReferenceLinkInput } from './types'
import { MARKETS } from './types'
import {
  ALLOWED_LINK_TYPES,
  REFERENCE_LINK_TYPE_LABELS,
  referenceLinkTypeLabel,
} from './linkStyles'
import {
  isValidDraft,
  marketLabel,
  newLink,
  syncDisplayOrder,
} from './validation'
import LinkFormDialog from './LinkFormDialog'
import { poolwerxColors } from '../../../theme/colors'
import { FreshdeskIcon } from '../../SystemIcons'
import {
  freshdeskMarketMismatch,
  isFreshdeskUrl,
} from '../../../utils/freshdeskUrls'
import {
  ADMIN_TABLE_HEADER_SX,
  INLINE_ACTION_NEUTRAL,
  inlineActionIconSx,
} from '../../admin/adminTableStyles'

interface ReferenceLinksEditorProps {
  links: ReferenceLinkInput[]
  onChange: (links: ReferenceLinkInput[]) => void
  allowedLinkTypes?: string[]
  availableMarkets?: readonly Market[]
  helperText?: string
}

const TEAL = poolwerxColors.teal
const RED = poolwerxColors.stockNegative
const NEUTRAL = INLINE_ACTION_NEUTRAL

const ReferenceLinksEditor: React.FC<ReferenceLinksEditorProps> = ({
  links,
  onChange,
  allowedLinkTypes = ALLOWED_LINK_TYPES,
  availableMarkets = MARKETS,
  helperText = 'Add per-market Fix Guide URLs surfaced inside the inspector Issues popover. Leave Market on "All markets" if the same link applies everywhere.',
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [draft, setDraft] = useState<ReferenceLinkInput | null>(null)

  const isEditing = editingIndex !== null
  const dialogOpen = isAdding || isEditing
  const overrideWarning = warningForOverrides(links)
  const mismatchWarning = warningForMarketMismatches(links)

  const startAdd = () => {
    setIsAdding(true)
    setEditingIndex(null)
    setDraft(newLink(allowedLinkTypes, links.length))
  }

  const startEdit = (index: number) => {
    setIsAdding(false)
    setEditingIndex(index)
    setDraft({ ...links[index] })
  }

  const cancelEdit = () => {
    setEditingIndex(null)
    setIsAdding(false)
    setDraft(null)
  }

  const saveEdit = () => {
    if (!draft || !isValidDraft(draft)) return
    const trimmed: ReferenceLinkInput = {
      ...draft,
      name: draft.name.trim(),
      url: draft.url.trim(),
      description: draft.description?.trim() || null,
    }
    if (isAdding) {
      onChange(syncDisplayOrder([...links, trimmed]))
    } else if (editingIndex !== null) {
      onChange(
        syncDisplayOrder(
          links.map((l, i) => (i === editingIndex ? trimmed : l)),
        ),
      )
    }
    cancelEdit()
  }

  const remove = (index: number) => {
    onChange(syncDisplayOrder(links.filter((_, i) => i !== index)))
    if (editingIndex === index) cancelEdit()
  }

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= links.length) return
    const next = [...links]
    const tmp = next[index]
    next[index] = next[target]
    next[target] = tmp
    onChange(syncDisplayOrder(next))
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={helperText ? 'space-between' : 'flex-end'}
        sx={{ mb: 1 }}
      >
        {helperText ? (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        ) : null}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={startAdd}
          disabled={dialogOpen}
          sx={{
            textTransform: 'none',
            borderColor: TEAL,
            color: TEAL,
            '&:hover': { borderColor: TEAL, backgroundColor: `${TEAL}10` },
          }}
        >
          Add link
        </Button>
      </Stack>
      {overrideWarning ? (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', mb: 1 }}
        >
          {overrideWarning}
        </Typography>
      ) : null}
      {mismatchWarning ? (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', mb: 1 }}
        >
          {mismatchWarning}
        </Typography>
      ) : null}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...ADMIN_TABLE_HEADER_SX, width: 72 }} aria-label="Reorder" />
              <TableCell sx={{ ...ADMIN_TABLE_HEADER_SX, width: 130 }}>Type</TableCell>
              <TableCell sx={{ ...ADMIN_TABLE_HEADER_SX, width: 140 }}>Market</TableCell>
              <TableCell sx={{ ...ADMIN_TABLE_HEADER_SX, width: 320 }}>Name</TableCell>
              <TableCell sx={ADMIN_TABLE_HEADER_SX}>URL</TableCell>
              <TableCell sx={{ ...ADMIN_TABLE_HEADER_SX, width: 100, textAlign: 'right' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No links yet. Click "Add link" to expose a Fix Guide on
                    the inspector Issues popover.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              links.map((link, index) => (
                <ViewRow
                  key={`view-${index}`}
                  link={link}
                  isFirst={index === 0}
                  isLast={index === links.length - 1}
                  disableActions={dialogOpen}
                  onEdit={() => startEdit(index)}
                  onMove={(d) => move(index, d)}
                  onRemove={() => remove(index)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {dialogOpen && draft ? (
        <LinkFormDialog
          open={dialogOpen}
          mode={isAdding ? 'add' : 'edit'}
          draft={draft}
          onDraftChange={setDraft}
          allowedLinkTypes={allowedLinkTypes}
          availableMarkets={availableMarkets}
          onSave={saveEdit}
          onCancel={cancelEdit}
        />
      ) : null}
    </Box>
  )
}

interface ViewRowProps {
  link: ReferenceLinkInput
  isFirst: boolean
  isLast: boolean
  disableActions: boolean
  onEdit: () => void
  onMove: (direction: -1 | 1) => void
  onRemove: () => void
}

const ViewRow: React.FC<ViewRowProps> = ({
  link,
  isFirst,
  isLast,
  disableActions,
  onEdit,
  onMove,
  onRemove,
}) => {
  // Stop a click on an interactive cell (reorder / URL / row actions) from also
  // bubbling to the row's edit handler.
  const stop = (e: React.MouseEvent) => e.stopPropagation()
  return (
    <TableRow
      hover
      onClick={disableActions ? undefined : onEdit}
      sx={{
        verticalAlign: 'middle',
        cursor: disableActions ? 'default' : 'pointer',
      }}
    >
      <TableCell sx={{ width: 72 }} onClick={stop}>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip title="Move up">
            <span>
              <IconButton
                size="small"
                disabled={disableActions || isFirst}
                onClick={() => onMove(-1)}
                sx={inlineActionIconSx(TEAL)}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton
                size="small"
                disabled={disableActions || isLast}
                onClick={() => onMove(1)}
                sx={inlineActionIconSx(TEAL)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={
            REFERENCE_LINK_TYPE_LABELS[link.link_type] ??
            referenceLinkTypeLabel(link.link_type)
          }
          sx={{
            height: 22,
            fontSize: '0.72rem',
            backgroundColor: `${TEAL}14`,
            color: TEAL,
          }}
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          variant="outlined"
          label={marketLabel(link.market)}
          sx={{
            height: 22,
            fontSize: '0.72rem',
            borderColor: link.market === null ? '#CBD5E1' : TEAL,
            color: link.market === null ? NEUTRAL : TEAL,
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {link.name}
        </Typography>
        {link.description ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {link.description}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell onClick={stop}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          {isFreshdeskUrl(link.url) ? (
            <Tooltip title="Freshdesk Solutions article">
              <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                <FreshdeskIcon size={16} />
              </Box>
            </Tooltip>
          ) : null}
          <Tooltip title={link.url}>
            <MuiLink
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                fontSize: '0.8rem',
                color: TEAL,
                display: 'block',
                maxWidth: 360,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {link.url}
            </MuiLink>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <span>
              <IconButton
                size="small"
                disabled={disableActions}
                onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                sx={inlineActionIconSx(TEAL)}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell sx={{ textAlign: 'right' }} onClick={stop}>
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Tooltip title="Edit">
            <span>
              <IconButton
                size="small"
                disabled={disableActions}
                onClick={onEdit}
                sx={inlineActionIconSx(TEAL)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove link">
            <span>
              <IconButton
                size="small"
                disabled={disableActions}
                onClick={onRemove}
                sx={inlineActionIconSx(RED)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  )
}

const warningForOverrides = (links: ReferenceLinkInput[]): string | null => {
  // Surface the "specific overrides global" rule. Group by link_type and
  // warn if a NULL-market row coexists with a market-specific row of the
  // same type.
  const byType: Record<string, { hasNull: boolean; hasSpecific: boolean }> = {}
  for (const link of links) {
    const slot = (byType[link.link_type] = byType[link.link_type] ?? {
      hasNull: false,
      hasSpecific: false,
    })
    if (link.market === null) slot.hasNull = true
    else slot.hasSpecific = true
  }
  const conflicts = Object.entries(byType).filter(([, s]) => s.hasNull && s.hasSpecific)
  if (conflicts.length === 0) return null
  const labels = conflicts.map(([t]) => referenceLinkTypeLabel(t)).join(', ')
  return `Heads up: ${labels} has both "All markets" and market-specific rows. The inspector will show both to matching FPs - delete the "All markets" entry if the market-specific ones should override.`
}

const warningForMarketMismatches = (
  links: ReferenceLinkInput[],
): string | null => {
  // Surface saved rows whose Freshdesk domain contradicts the selected market
  // (the hard mismatch case), so it's visible without entering edit mode. The
  // softer "All markets" nudge stays per-row only.
  const offenders = links.filter(
    (l) => freshdeskMarketMismatch(l.url, l.market)?.kind === 'mismatch',
  )
  if (offenders.length === 0) return null
  const names = offenders.map((l) => l.name.trim() || l.url).join(', ')
  const verb = offenders.length === 1 ? 'points' : 'point'
  return `Market mismatch: ${names} ${verb} to a Freshdesk domain that doesn't match the row's selected market. Open the row to fix.`
}

export default ReferenceLinksEditor
